import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight, Sparkles } from "lucide-react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { services } from "@/data/services";

/* ─── Data ─── */
const resourceLinks = [
  { name: "Income Tax Calculator", id: "income-tax" },
  { name: "HRA Calculator", id: "hra" },
  { name: "GST Calculator", id: "gst" },
  { name: "TDS Calculator", id: "tds" },
  { name: "EMI Calculator", id: "emi" },
  { name: "Compliance Calendar", id: "calendar" },
];

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services", hasDropdown: "services" },
  { name: "Insights", path: "/blogs" },
  { name: "Resources", path: "/resources", hasDropdown: "resources" },
  { name: "Contact", path: "/contact" },
];

/* ─── Magnetic Link ─── */
function MagneticLink({ children, className, ...props }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 260, damping: 18 });
  const springY = useSpring(y, { stiffness: 260, damping: 18 });

  return (
    <motion.div
      ref={ref}
      onMouseMove={(e) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        x.set((e.clientX - rect.left - rect.width / 2) * 0.12);
        y.set((e.clientY - rect.top - rect.height / 2) * 0.12);
      }}
      onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ x: springX, y: springY }}
      className="inline-block"
    >
      <Link className={className} {...props}>{children}</Link>
    </motion.div>
  );
}

/* ─── Sliding Active Indicator ─── */
function NavIndicator({ activeIndex, linkRefs }) {
  const [pos, setPos] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const el = linkRefs.current[activeIndex];
    if (el) {
      const parent = el.closest("[data-nav-container]");
      const parentRect = parent?.getBoundingClientRect() || { left: 0 };
      const rect = el.getBoundingClientRect();
      setPos({ left: rect.left - parentRect.left, width: rect.width });
    }
  }, [activeIndex, linkRefs]);

  if (activeIndex < 0) return null;
  return (
    <motion.div
      className="absolute bottom-0 h-[2px] rounded-full"
      style={{ background: "linear-gradient(90deg, transparent, #D97706, #FBBF24, #D97706, transparent)" }}
      animate={{ left: pos.left, width: pos.width }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
    />
  );
}

/* ─── Dropdown Panel ─── */
function DropdownPanel({ type }) {
  const items = type === "services"
    ? services.map(s => ({ label: s.title, to: `/services/${s.slug}`, key: s.id }))
    : resourceLinks.map(r => ({ label: r.name, to: `/resources?tab=${r.id}`, key: r.id }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 14, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.97 }}
      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
      className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[280px]"
      style={{ perspective: 900, transformOrigin: "top center" }}
    >
      <div
        className="rounded-2xl overflow-hidden border border-white/[0.07] shadow-2xl shadow-black/50"
        style={{
          background: "linear-gradient(180deg, rgba(10,15,30,0.92) 0%, rgba(6,11,24,0.97) 100%)",
          backdropFilter: "blur(32px) saturate(180%)",
          WebkitBackdropFilter: "blur(32px) saturate(180%)",
        }}
      >
        {/* Top shimmer line */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-[#D97706]/30 to-transparent" />
        <div className="py-2">
          {items.map((item, i) => (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.035, duration: 0.25 }}
            >
              <Link
                to={item.to}
                className="group/item flex items-center gap-3 px-5 py-3 text-[13px] text-white/50 hover:text-[#FDFBF7] transition-all duration-250 font-body relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#D97706]/0 to-transparent group-hover/item:from-[#D97706]/[0.07] transition-all duration-300" />
                <div className="w-1 h-1 rounded-full bg-[#D97706]/30 group-hover/item:bg-[#FBBF24] group-hover/item:shadow-[0_0_6px_rgba(251,191,36,0.5)] transition-all duration-300 flex-shrink-0" />
                <span className="relative z-10">{item.label}</span>
                <ArrowRight size={11} className="ml-auto opacity-0 -translate-x-2 group-hover/item:opacity-40 group-hover/item:translate-x-0 transition-all duration-300" />
              </Link>
            </motion.div>
          ))}
        </div>
        {/* Bottom shimmer line */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════
   NAVBAR — Cinematic Glassmorphic
═══════════════════════════════════════════ */
export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [hoveredIdx, setHoveredIdx] = useState(-1);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const location = useLocation();
  const linkRefs = useRef([]);
  const scrollY = useMotionValue(0);

  const activeIndex = navLinks.findIndex(l =>
    l.path === "/" ? location.pathname === "/" : location.pathname.startsWith(l.path)
  );

  /* Scroll tracking */
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      scrollY.set(window.scrollY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [scrollY]);

  /* Reset on navigate */
  useEffect(() => {
    setIsOpen(false);
    setOpenDropdown(null);
    setMobileDropdown(null);
  }, [location]);

  const indicatorIdx = hoveredIdx >= 0 ? hoveredIdx : activeIndex;

  /* Spring-animated background opacity for glass */
  const bgOpacity = useSpring(useTransform(scrollY, [0, 80], [0.35, 0.92]), { stiffness: 200, damping: 30 });
  const blurAmount = useSpring(useTransform(scrollY, [0, 80], [12, 28]), { stiffness: 200, damping: 30 });

  return (
    <>
      <motion.nav
        className="sticky top-0 z-[60]"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        data-testid="main-navbar"
      >
        {/* ── Layer 1: Glass Background ── */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundColor: useTransform(bgOpacity, v => `rgba(6,11,24,${v})`),
            backdropFilter: useTransform(blurAmount, v => `blur(${v}px) saturate(${140 + v * 2.5}%)`),
            WebkitBackdropFilter: useTransform(blurAmount, v => `blur(${v}px) saturate(${140 + v * 2.5}%)`),
          }}
        />

        {/* ── Layer 2: Ambient light refraction (top edge) ── */}
        <div
          className="absolute top-0 left-0 right-0 h-[1px] pointer-events-none"
          style={{
            background: "linear-gradient(90deg, transparent 10%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.04) 70%, transparent 90%)",
            opacity: scrolled ? 0.8 : 0.3,
            transition: "opacity 0.5s ease",
          }}
        />

        {/* ── Layer 3: Bottom border glow (animated sweep) ── */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-[1px] pointer-events-none overflow-hidden"
          animate={{
            opacity: scrolled ? 1 : 0.25,
          }}
          transition={{ duration: 0.5 }}
        >
          <div
            style={{
              height: "100%",
              background: scrolled
                ? "linear-gradient(90deg, transparent 2%, rgba(217,119,6,0.12) 20%, rgba(251,191,36,0.25) 50%, rgba(217,119,6,0.12) 80%, transparent 98%)"
                : "linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent)",
              transition: "background 0.6s ease",
            }}
          />
        </motion.div>

        {/* ── Layer 4: Subtle ambient glow underneath ── */}
        <motion.div
          className="absolute -bottom-8 left-[15%] right-[15%] h-8 pointer-events-none rounded-full"
          animate={{
            opacity: scrolled ? 0.15 : 0,
            background: "radial-gradient(ellipse at center, rgba(217,119,6,0.25) 0%, transparent 70%)",
          }}
          transition={{ duration: 0.6 }}
        />

        {/* ── Content ── */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-16 lg:h-[72px] relative z-10">

          {/* Logo */}
          <motion.div whileHover={{ scale: 1.04 }} transition={{ type: "spring", stiffness: 400 }}>
            <Link to="/" className="flex items-center group" data-testid="nav-logo">
              <motion.div
                className="relative"
                whileHover={{ rotateY: 8, rotateX: -4 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ perspective: 600, transformStyle: "preserve-3d" }}
              >
                <div className="absolute inset-0 -m-3 rounded-xl bg-[#D97706]/15 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <img
                  src="/logo_transparent.png"
                  alt="AS Consultech India Private Limited"
                  className="h-10 md:h-12 w-auto object-contain relative z-10"
                />
              </motion.div>
            </Link>
          </motion.div>

          {/* ── Desktop Navigation ── */}
          <nav className="hidden lg:flex items-center gap-2">
            <div
              className="flex items-center gap-0.5 rounded-full px-1.5 py-1.5 relative"
              data-nav-container
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <NavIndicator activeIndex={indicatorIdx} linkRefs={linkRefs} />

              {navLinks.map((link, i) =>
                link.hasDropdown ? (
                  <div
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => { setOpenDropdown(link.hasDropdown); setHoveredIdx(i); }}
                    onMouseLeave={() => { setOpenDropdown(null); setHoveredIdx(-1); }}
                    ref={el => linkRefs.current[i] = el}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center gap-1 px-4 py-2.5 text-[13px] font-body font-medium transition-all duration-300 rounded-full ${
                        location.pathname.startsWith(link.path)
                          ? "text-[#FDFBF7]"
                          : "text-white/45 hover:text-[#FDFBF7]"
                      }`}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, "-")}-link`}
                    >
                      {link.name}
                      <motion.span
                        animate={{ rotate: openDropdown === link.hasDropdown ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="inline-flex"
                      >
                        <ChevronDown size={12} />
                      </motion.span>
                    </Link>
                    <AnimatePresence>
                      {openDropdown === link.hasDropdown && (
                        <DropdownPanel type={link.hasDropdown} />
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div
                    key={link.name}
                    ref={el => linkRefs.current[i] = el}
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(-1)}
                  >
                    <MagneticLink
                      to={link.path}
                      className={`block px-4 py-2.5 text-[13px] font-body font-medium transition-all duration-300 rounded-full ${
                        location.pathname === link.path
                          ? "text-[#FDFBF7]"
                          : "text-white/45 hover:text-[#FDFBF7]"
                      }`}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, "-")}-link`}
                    >
                      {link.name}
                    </MagneticLink>
                  </div>
                )
              )}

              {/* CTA Button */}
              <Link
                to="/contact"
                className="group/cta ml-1.5 inline-flex items-center gap-2 rounded-full px-5 py-2 text-[13px] font-heading font-bold text-[#0A0F1C] relative overflow-hidden transition-all duration-300 hover:shadow-[0_0_24px_rgba(217,119,6,0.35)]"
                style={{ background: "linear-gradient(135deg, #FBBF24 0%, #D97706 100%)" }}
                data-testid="nav-cta-button"
              >
                {/* Shimmer sweep */}
                <div className="absolute inset-0 -translate-x-full group-hover/cta:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-[-15deg]" />
                <Sparkles size={13} className="relative z-10" />
                <span className="relative z-10">Book Consultation</span>
                <ArrowRight size={13} className="relative z-10 transition-transform duration-300 group-hover/cta:translate-x-0.5" />
              </Link>
            </div>
          </nav>

          {/* Mobile Toggle */}
          <motion.button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2.5 text-[#FDFBF7] rounded-xl transition-colors relative overflow-hidden"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
            data-testid="mobile-menu-toggle"
            whileTap={{ scale: 0.9 }}
            whileHover={{ background: "rgba(255,255,255,0.1)" }}
          >
            <AnimatePresence mode="wait">
              {isOpen ? (
                <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <X size={22} />
                </motion.div>
              ) : (
                <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <Menu size={22} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* ── Mobile Drawer ── */}
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="fixed inset-0 z-[45] lg:hidden"
                style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)" }}
                onClick={() => setIsOpen(false)}
              />

              {/* Drawer Panel */}
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="fixed top-0 right-0 bottom-0 w-[85vw] max-w-[400px] z-[50] lg:hidden flex flex-col overflow-hidden"
                style={{
                  background: "linear-gradient(180deg, rgba(8,12,26,0.97) 0%, rgba(5,10,21,0.99) 100%)",
                  backdropFilter: "blur(40px) saturate(180%)",
                  WebkitBackdropFilter: "blur(40px) saturate(180%)",
                  borderLeft: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                {/* Ambient glow at top */}
                <div className="absolute top-0 left-0 right-0 h-40 pointer-events-none" style={{ background: "radial-gradient(ellipse at top right, rgba(217,119,6,0.08) 0%, transparent 60%)" }} />

                {/* Header */}
                <div className="flex items-center justify-between px-6 h-16 sm:h-[72px] border-b border-white/[0.06] flex-shrink-0 relative z-10">
                  <span className="text-[#FDFBF7] font-heading font-bold text-lg tracking-tight">Menu</span>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 text-white/50 hover:text-white hover:bg-white/[0.06] rounded-lg transition-all duration-200"
                  >
                    <X size={22} />
                  </button>
                </div>

                {/* Links */}
                <div className="flex-1 overflow-y-auto px-6 py-8 space-y-1 relative z-10">
                  {navLinks.map((link, i) => (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.08 + i * 0.06, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="border-b border-white/[0.03] last:border-0"
                    >
                      {link.hasDropdown ? (
                        <div>
                          <button
                            onClick={() => setMobileDropdown(mobileDropdown === link.hasDropdown ? null : link.hasDropdown)}
                            className={`flex justify-between items-center py-4 text-lg font-heading font-semibold transition-colors w-full text-left ${
                              mobileDropdown === link.hasDropdown ? "text-[#FBBF24]" : "text-[#FDFBF7]"
                            }`}
                          >
                            {link.name}
                            <motion.span animate={{ rotate: mobileDropdown === link.hasDropdown ? 180 : 0 }} transition={{ duration: 0.3 }}>
                              <ChevronDown size={18} />
                            </motion.span>
                          </button>
                          <AnimatePresence>
                            {mobileDropdown === link.hasDropdown && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                className="overflow-hidden"
                              >
                                <div className="pl-4 border-l border-[#D97706]/25 mt-1 space-y-1 mb-4">
                                  {link.hasDropdown === "services" && services.map((s) => (
                                    <Link key={s.id} to={`/services/${s.slug}`} className="block py-2 text-sm text-white/40 hover:text-[#FBBF24] transition-colors">
                                      {s.title}
                                    </Link>
                                  ))}
                                  {link.hasDropdown === "resources" && resourceLinks.map((r) => (
                                    <Link key={r.id} to={`/resources?tab=${r.id}`} className="block py-2 text-sm text-white/40 hover:text-[#FBBF24] transition-colors">
                                      {r.name}
                                    </Link>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <Link
                          to={link.path}
                          className={`block py-4 text-lg font-heading font-semibold transition-colors ${
                            (link.path === "/" ? location.pathname === "/" : location.pathname.startsWith(link.path))
                              ? "text-[#FBBF24]"
                              : "text-[#FDFBF7] hover:text-[#FBBF24]"
                          }`}
                        >
                          {link.name}
                        </Link>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Mobile CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.35 }}
                  className="p-6 border-t border-white/[0.06] flex-shrink-0 relative z-10"
                >
                  <Link
                    to="/contact"
                    className="group flex justify-center items-center gap-2.5 w-full py-4 rounded-xl font-heading font-bold text-[#0A0F1C] relative overflow-hidden transition-all hover:shadow-[0_0_28px_rgba(217,119,6,0.35)]"
                    style={{ background: "linear-gradient(135deg, #FBBF24 0%, #D97706 100%)" }}
                  >
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-15deg]" />
                    <Sparkles size={16} className="relative z-10" />
                    <span className="relative z-10">Book Free Consultation</span>
                    <ArrowRight size={16} className="relative z-10" />
                  </Link>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
}
